// src/ui/usePullToRefresh.js — drag the feed down to refresh it.
//
// 🟥 THE BROWSER HAD NO WAY TO REFRESH THE FEED AT ALL. Not a gesture, not a
//    button, not a re-fetch on focus — the only thing that ever added posts was
//    the load-more sentinel at the bottom, which appends and never replaces. On
//    a phone-shaped browser the gesture people reach for is the one the app has,
//    and it did nothing.
//
// 🟥 AND THE PAGE DOES NOT SCROLL — `.vb-feed` does. `.vibes-shell` is
//    `height: 100%`, so listening on `window` would hear nothing at all. The
//    hook attaches to the scroller it is given.
//
// 🟥 THE FIRST VERSION LISTENED FOR TOUCH AND NOTHING ELSE, so on a desktop —
//    which is where most people read this client — pulling the feed down did
//    literally nothing. A mouse and a trackpad do not produce touch events. The
//    same gesture arrives there as a `wheel` with a negative deltaY at a
//    scroller with nowhere left to go, and it is handled below.
//
// 🟥 AND IT BOUND ITS LISTENERS IN AN EFFECT KEYED ON A REF OBJECT — which is
//    the same object for ever, so the effect ran once and never again. An
//    element that mounted later (a feed behind a gate, behind a loading branch)
//    got no listeners at all, for the whole session. It is a callback ref now.
//
// 🟢 A GESTURE, NOT A LIBRARY. Three touch handlers and a number: how far the
//    finger has travelled from a scroll position that was already at the top.
//    Everything visual is left to the caller, which is what makes it possible
//    for the indicator to match the product rather than a package's idea of it.

import { useCallback, useEffect, useRef, useState } from 'react';

/** How far to pull before it counts. Short enough to discover, long enough not to fire on a flick. */
export const PULL_TRIGGER = 72;
/** How far the indicator is allowed to travel — past the trigger it resists. */
export const PULL_MAX = 110;

/**
 * 🟥 THE RESISTANCE IS THE AFFORDANCE. A 1:1 pull hits the trigger before the
 *    reader has decided they meant it; a pull that gets heavier says "keep
 *    going" and then "that's enough" without a word of copy.
 */
export function pullDistance(rawDelta) {
  const d = Math.max(0, Number(rawDelta) || 0);
  if (d <= PULL_TRIGGER) return d;
  /* past the trigger, each further pixel is worth a third of one */
  return Math.min(PULL_MAX, PULL_TRIGGER + (d - PULL_TRIGGER) / 3);
}

/** Has this pull earned a refresh? */
export const pullArmed = (distance) => (Number(distance) || 0) >= PULL_TRIGGER;

/**
 * @param ref      the SCROLLING element (not the window)
 * @param onRefresh async; the indicator stays until it settles
 * @param enabled  false while another view owns the surface
 */
/**
 * @param onRefresh async; the indicator stays until it settles
 * @param enabled   false while another view owns the surface
 * @returns { attach, distance, busy, armed, refresh }
 *
 * 🟥 IT WAS A ref PARAMETER AND AN EFFECT THAT RAN ONCE. `useEffect(…, [ref])`
 *    never re-runs — a ref object is the same object for ever — so if the
 *    element was not mounted on the pass that ran it, the listeners were never
 *    attached and the gesture was dead for the whole session. A feed behind a
 *    gate, a loading branch, or any conditional render is exactly that case.
 *
 * 🟢 A CALLBACK REF BINDS WHEN THE NODE APPEARS, and re-binds when it is
 *    replaced. There is no ordering to get right because there is no ordering.
 */
export default function usePullToRefresh(onRefresh, enabled = true) {
  const [distance, setDistance] = useState(0);
  const [busy, setBusy] = useState(false);
  const elRef = useRef(null);
  const offRef = useRef(null);
  const startRef = useRef(null);
  const distRef = useRef(0);
  const busyRef = useRef(false);
  const wheelRef = useRef(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  /* 🟢 THE LIVE DISTANCE LIVES IN A REF. The listeners are attached once and
     close over the first render's `distance` for ever — which is why the old
     code had to read it inside a `setDistance` updater and call `run()` from
     there. An updater that has side effects is invoked twice under StrictMode
     and is a lie about what a state setter is; a ref is simply the answer. */
  const put = useCallback((d) => { distRef.current = d; setDistance(d); }, []);

  const run = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try { await onRefreshRef.current?.(); } catch { /* the indicator still has to end */ } finally {
      busyRef.current = false;
      setBusy(false);
      put(0);
    }
  }, [put]);

  const release = useCallback(() => {
    if (pullArmed(distRef.current)) run();
    else put(0);
  }, [run, put]);

  const attach = useCallback((el) => {
    if (offRef.current) { offRef.current(); offRef.current = null; }
    elRef.current = el;
    if (!el) return;

    const atTop = () => el.scrollTop <= 0;

    /* ── touch ── */
    const onStart = (e) => {
      /* 🟥 ONLY FROM THE TOP. Starting a pull from halfway down the feed would
         fight the scroll the reader is actually doing — the gesture is only
         unambiguous when there is nowhere left to scroll up to. */
      startRef.current = atTop() ? (e.touches?.[0]?.clientY ?? null) : null;
    };
    const onMove = (e) => {
      if (startRef.current == null || busyRef.current) return;
      const y = e.touches?.[0]?.clientY ?? 0;
      const d = pullDistance(y - startRef.current);
      /* 🟢 A SCROLL UPWARDS CANCELS IT, rather than leaving a stuck indicator
         behind the content the reader has moved on to. */
      if (d <= 0) { startRef.current = null; put(0); return; }
      put(d);
    };
    const onEnd = () => {
      if (startRef.current == null) return;
      startRef.current = null;
      release();
    };

    /* ── wheel / trackpad ──────────────────────────────────────────────────
       🟥 THE GESTURE DID NOT EXIST ON A DESKTOP AT ALL. Touch events are not
          produced by a mouse or a trackpad, so on the machine most people read
          the web client on, pulling the feed down did precisely nothing — which
          is what was reported. A two-finger swipe past the top of a scroller is
          a `wheel` with a negative deltaY and nowhere to scroll; that is the
          same gesture, arriving under a different name.

       🟢 AND IT SETTLES RATHER THAN FIRING. A wheel has no "finger lifted", so
          the end of the gesture is a short silence — long enough that one flick
          of a trackpad is not mistaken for a release halfway through. */
    const onWheel = (e) => {
      if (busyRef.current || !atTop()) return;
      if (e.deltaY >= 0) {
        /* scrolling back down abandons the pull immediately, rather than
           leaving the indicator open above content being read */
        if (distRef.current) put(0);
        return;
      }
      put(pullDistance((distRef.current || 0) + -e.deltaY));
      if (wheelRef.current) clearTimeout(wheelRef.current);
      wheelRef.current = setTimeout(() => { wheelRef.current = null; release(); }, 180);
    };

    /* passive: the page never scrolls, so there is nothing to preventDefault */
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    el.addEventListener('wheel', onWheel, { passive: true });
    offRef.current = () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('wheel', onWheel);
      if (wheelRef.current) { clearTimeout(wheelRef.current); wheelRef.current = null; }
    };
  }, [put, release]);

  /* 🟢 DISABLED MEANS DETACHED, not "attached and ignoring everything" — a
     listener that runs on every wheel event to decide it is switched off is a
     cost paid on the surface that is scrolled most. */
  useEffect(() => {
    if (enabled) return undefined;
    if (offRef.current) { offRef.current(); offRef.current = null; }
    return undefined;
  }, [enabled]);

  useEffect(() => () => { if (offRef.current) offRef.current(); }, []);

  return { attach, distance, busy, armed: pullArmed(distance), refresh: run };
}
